import { useEffect, useState } from "react";
import { Eye, EyeOff, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { apiClient, getPublicSettings, googleLogin } from "../../api/apiServices";
import { useAuth } from "../../context/useAuth";
import GoogleSignInButton from "../../components/GoogleSignInButton";

export default function LoginPage() {
  const navigate = useNavigate();

  const { login } = useAuth();

  const [form, setForm] = useState({
    identifier: "",
    password: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [googleError, setGoogleError] = useState("");

  // Whether "Continue with Google" shows here is decided by the admin in
  // Platform Settings, not hardcoded — see the signup screen for the same
  // pattern.
  const [settings, setSettings] = useState({
    googleLoginEnabled: false,
    googleClientId: null,
  });

  useEffect(() => {
    getPublicSettings()
      .then((data) => setSettings((prev) => ({ ...prev, ...data })))
      .catch(() => {});
  }, []);

  const handleGoogleIdToken = async (idToken) => {
    setGoogleError("");
    try {
      const data = await googleLogin(idToken);
      login(data.token, data.user);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setGoogleError(err.response?.data?.message || "Google sign-in failed. Please try again.");
    }
  };

  const isValid = form.identifier.trim().length > 3 && form.password.length >= 6 && !loading;

  const handleSubmit = async (e) => {
    if (e) e.preventDefault(); // This is critical to stop the reload
    if (loading || !isValid) return;

    setError("");
    setLoading(true);

    const trimmedIdentifier = form.identifier.trim();
    const payload = { password: form.password.trim() };

    if (trimmedIdentifier.includes("@")) {
      payload.email = trimmedIdentifier.toLowerCase();
    } else {
      let phone = trimmedIdentifier.replace(/\D/g, "");
      if (phone.startsWith("0")) phone = "+234" + phone.slice(1);
      else if (!phone.startsWith("+")) phone = "+234" + phone;
      payload.phone = phone;
    }

    try {
      const response = await apiClient.post("/auth/login", payload);

      const token = response.data?.token || response.data?.accessToken;
      const userData = response.data?.user;

      if (!token) throw new Error("No session token received.");

      login(token, userData);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      console.error("Login Error:", err);

      const backendMessage = err.response?.data?.message;

      // Unverified accounts come back as a 401 with a specific message, same
      // status code as "wrong password" — so we have to check the message
      // itself, not just the status, or this always gets swallowed by the
      // generic "Invalid email/phone or password." below.
      if (backendMessage && /not\s*verified/i.test(backendMessage)) {
        if (payload.email) {
          navigate("/verify", { state: { email: payload.email } });
        } else {
          // The verify screen only knows how to display/resend to an email
          // address (see VerifyCode.jsx: location.state?.email). If they
          // logged in with a phone number we have no email to hand it, so
          // we can't route them there blind — surface it as a message instead.
          setError("Your email hasn't been verified yet. Please log in with your email to continue verification.");
        }
        setLoading(false);
        return;
      }

      let message = "Something went wrong. Please try again.";
      if (err.response) {
        message = backendMessage || (err.response.status === 401
          ? "Invalid email/phone or password."
          : message);
      } else if (err.request) {
        message = "Network error. Please check your connection.";
      }
      setError(message);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F6FA] flex flex-col px-5 pt-6 pb-10 max-w-md mx-auto">
      {/* Header */}
      <div className="flex items-center mb-8">
        <button 
          type="button" 
          onClick={() => navigate(-1)} 
          className="p-2 -ml-2 rounded-full hover:bg-gray-100"
        >
          <ArrowLeft size={24} className="text-gray-700" />
        </button>
      </div>

      <h1 className="text-3xl font-bold text-gray-900 mb-2">Welcome back</h1>
      <p className="text-gray-600 mb-10">Log in to your account</p>

      {/* Main Form */}
      <form onSubmit={handleSubmit} className="flex-1 flex flex-col">
        <div className="space-y-6 flex-1">
          {/* Identifier Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Phone number / Email
            </label>
            <input
              type="text"
              placeholder="08012345678 or example@email.com"
              value={form.identifier}
              onChange={(e) => setForm({ ...form, identifier: e.target.value })}
              className={`w-full rounded-full px-5 py-4 bg-white border-2 transition-all outline-none ${
                error ? "border-red-500" : "border-gray-300 focus:border-blue-500"
              }`}
            />
          </div>

          {/* Password Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
            <div className={`flex items-center rounded-full border-2 px-5 py-4 bg-white ${
              error ? "border-red-500" : "border-gray-300 focus-within:border-blue-500"
            }`}>
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Enter password"
                value={form.password}
                onChange={(e) => {
                  setForm({ ...form, password: e.target.value });
                  setError("");
                }}
                className="flex-1 bg-transparent outline-none text-gray-900"
              />
              <button 
                type="button" 
                onClick={() => setShowPassword(!showPassword)} 
                className="text-gray-500"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            
            <div className="text-right mt-3">
              <button
                type="button"
                onClick={() => navigate("/forgot-password")}
                className="text-sm text-blue-600 font-medium"
              >
                Forgot password?
              </button>
            </div>
          </div>

          {/* Inline Error Message */}
          {error && <p className="text-sm text-red-600 font-medium text-center">{error}</p>}
        </div>

        {/* Bottom Actions */}
        <div className="mt-10">
          <button
            type="submit"
            disabled={!isValid}
            className={`w-full py-4 rounded-full font-semibold shadow-md transition-all ${
              isValid ? "bg-[#344054] text-white" : "bg-gray-300 text-gray-500 cursor-not-allowed"
            }`}
          >
            {loading ? "Logging in..." : "Log in"}
          </button>

          {settings.googleLoginEnabled && settings.googleClientId && (
            <div className="pt-1">
              <div className="flex items-center my-6">
                <div className="flex-1 h-[1px] bg-gray-200"></div>
                <span className="px-3 text-gray-400 text-xs font-medium uppercase">Or</span>
                <div className="flex-1 h-[1px] bg-gray-200"></div>
              </div>
              <GoogleSignInButton
                clientId={settings.googleClientId}
                onIdToken={handleGoogleIdToken}
                onError={(err) => setGoogleError(err.message)}
                text="continue_with"
              />
              {googleError && (
                <p className="text-sm text-red-600 mt-3 font-medium text-center">{googleError}</p>
              )}
            </div>
          )}

          <div className="flex items-center my-6">
            <div className="flex-1 h-[1px] bg-gray-200"></div>
            <span className="px-3 text-gray-400 text-xs font-medium uppercase">New here?</span>
            <div className="flex-1 h-[1px] bg-gray-200"></div>
          </div>

          <button
            type="button"
            onClick={() => navigate("/signup")}
            className="w-full py-4 rounded-full font-semibold border-2 border-[#344054] text-[#344054] hover:bg-gray-50"
          >
            Create Account
          </button>

          <p className="text-[10px] text-gray-500 text-center mt-6 px-4">
            By logging in, you agree to our <span className="underline">Terms</span> and <span className="underline">Privacy Policy</span>
          </p>
        </div>
      </form>
    </div>
  );
}